import { ArrowLeft, CircleAlert } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { PageHeader, ServiceMark, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/** Rozvržení stránky propojení banky: vlevo postup, vpravo formulář, pod tím nalezené účty. */
export const BankConnectLayout = ({
  title,
  service,
  description,
  steps,
  note,
  children,
  accounts,
}: {
  title: string
  /** slug banky pro značku v nadpisu (fio, moneta, raiffeisen, erste) */
  service?: string
  description: string
  steps: ReactNode[]
  note?: ReactNode
  children: ReactNode
  accounts?: ReactNode
}) => (
  <div className="mx-auto w-full max-w-[1080px]">
    <Link to="/bank" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" />
      Banka
    </Link>
    <PageHeader
      title={
        <span className="flex items-center gap-3">
          {service && <ServiceMark service={service} size="lg" />}
          {title}
        </span>
      }
      description={description}
    />

    <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
      <section aria-labelledby="bank-steps">
        <h2 id="bank-steps" className="mb-3 text-sm font-medium">
          Postup
        </h2>
        {note && <p className="mb-4 rounded-lg bg-muted px-4 py-3 text-sm text-pretty">{note}</p>}
        <ol className="space-y-3">
          {steps.map((step, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-xs font-medium tabular">{i + 1}</span>
              <span className="pt-0.5 text-pretty">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="h-fit rounded-lg border p-5">{children}</section>
    </div>

    {accounts && <div className="mt-8">{accounts}</div>}
  </div>
)

/** Hláška o stavu spojení s bankou (vrací ji API jako connection). */
export const ConnectionNotice = ({ connection }: { connection?: 'n/a' | 'failed' | 'success' }) =>
  connection === 'failed' ? (
    <div className="mb-4 flex gap-2 rounded-md bg-danger-bg px-3 py-2.5 text-sm text-danger">
      <CircleAlert className="mt-0.5 size-4 shrink-0" />
      Spojení s bankou se nepodařilo ověřit. Zkontrolujte přihlašovací údaje a zkuste to znovu.
    </div>
  ) : null

export interface AvailableAccount {
  accountId: string | number
  accountName?: string | null
  friendlyName?: string | null
  accountNumber: string
  accountNumberPrefix: string | null
  bankCode: string
  mainCurrency?: string | null
}

/** Účty, které banka zpřístupnila, s akcí Přidat. */
export const AvailableAccounts = ({
  accounts,
  knownAccounts,
  loading,
  onAdd,
  emptyText,
}: {
  accounts: AvailableAccount[] | undefined
  knownAccounts: string[] | undefined
  loading?: boolean
  onAdd: (accountId: AvailableAccount['accountId']) => Promise<unknown>
  emptyText: string
}) => {
  const [adding, setAdding] = useState<AvailableAccount['accountId'] | null>(null)

  const add = async (id: AvailableAccount['accountId']) => {
    setAdding(id)
    try {
      await onAdd(id)
      toast.success('Účet byl přidán')
    } catch {
      toast.error('Účet se nepodařilo přidat')
    } finally {
      setAdding(null)
    }
  }

  return (
    <section aria-labelledby="bank-accounts">
      <h2 id="bank-accounts" className="mb-3 text-sm font-medium">
        Účty v bance
      </h2>
      <div className="overflow-hidden rounded-lg border">
        {loading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        )}
        {!loading && !accounts?.length && <p className="px-4 py-6 text-sm text-muted-foreground">{emptyText}</p>}
        {!loading && !!accounts?.length && (
          <ul className="divide-y">
            {accounts.map((a) => {
              const known = knownAccounts?.includes(a.accountNumber)
              const name = a.friendlyName || a.accountName
              return (
                <li key={a.accountId} className="flex min-h-11 items-center justify-between gap-4 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm tabular">
                      {a.accountNumberPrefix && `${a.accountNumberPrefix}-`}
                      {a.accountNumber}/{a.bankCode}
                    </p>
                    {(name || a.mainCurrency) && (
                      <p className="truncate text-xs text-muted-foreground">{[name, a.mainCurrency].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                  {known ? (
                    <StatusBadge status="matched" label="Přidáno" />
                  ) : (
                    <Button variant="outline" size="sm" disabled={adding !== null} onClick={() => add(a.accountId)}>
                      {adding === a.accountId ? 'Přidávám' : 'Přidat'}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}
